import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { MastermindSpace } from "@shared/schema";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useKnowledgeBaseList } from "@/contexts/KnowledgeBaseContext";
import type { KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { useAllSpacesMemberKnowledgeDocuments } from "@/hooks/useSharedSpaceKnowledgeDocuments";

export interface UseMergedKnowledgeDocumentsOptions {
  enabled?: boolean;
}

export function useMergedKnowledgeDocuments(
  options?: UseMergedKnowledgeDocumentsOptions
) {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const enabled = options?.enabled !== false;

  const {
    items: ownDocuments,
    isLoading: ownLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useKnowledgeBaseList({ enabled: enabled && !!user });

  const { data: spaces = [], isLoading: spacesLoading } = useQuery<
    MastermindSpace[]
  >({
    queryKey: ["/api/spaces", user?.id, currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      if (!user) return [];
      return await mastermindSpacesService.getMastermindSpaces(
        currentWorkspaceId ?? null
      );
    },
    enabled: enabled && !!user,
    staleTime: 60_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    memberDocuments,
    documentsBySpaceId,
    isLoading: memberLoading,
  } = useAllSpacesMemberKnowledgeDocuments(spaces, { enabled: enabled && !!user });

  const allDocuments = useMemo(() => {
    const seen = new Set<string>();
    const result: KnowledgeBaseItem[] = [];
    for (const doc of ownDocuments) {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        result.push(doc);
      }
    }
    for (const doc of memberDocuments) {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        result.push(doc);
      }
    }
    return result;
  }, [ownDocuments, memberDocuments]);

  return {
    allDocuments,
    ownDocuments,
    memberDocuments,
    documentsBySpaceId,
    spaces,
    isLoading: ownLoading || spacesLoading || memberLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
}
